import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Loader from '../../components/Common/Loader';
import { ArrowLeft, Percent, Calendar, Building2 } from 'lucide-react';

interface ObligationDetails {
    symbole: string;
    nom_obligation: string | null;
    emetteur: string | null;
    taux_interet: number | null;
    date_echeance: string | null;
    cotations: {
        date_cotation: string;
        cours_cloture: number | null;
        cours_pied_coupon: number | null;
        coupon_couru: number | null;
        volume_titres: number | null;
        volume_fcfa: number | null;
    }[];
}

const ObligationDetailsScreen: React.FC = () => {
    const { symbole } = useParams<{ symbole: string }>();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [details, setDetails] = useState<ObligationDetails | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token && symbole) fetchDetails();
    }, [token, symbole]);

    const fetchDetails = async () => {
        try {
            const response = await fetch(`${API_URL}/v1/obligations/${symbole}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setDetails(data);
            }
        } catch (error) {
            console.error('Error fetching obligation details:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <SiteLayout><Loader message="Chargement..." /></SiteLayout>;
    if (!details) return <SiteLayout><div style={{ padding: '40px', textAlign: 'center' }}>Obligation non trouvée</div></SiteLayout>;

    const latestCours = details.cotations[0]?.cours_cloture;
    const latestVolume = details.cotations[0]?.volume_fcfa;

    return (
        <SiteLayout>
            <Header title="Détails Obligation" />
            <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
                <button
                    onClick={() => navigate('/obligations')}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#f3f4f6', cursor: 'pointer' }}
                >
                    <ArrowLeft size={18} /> Retour à la liste
                </button>

                <div style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '20px',
                    padding: '30px',
                    color: 'white',
                    marginBottom: '24px',
                }}>
                    <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>{details.symbole}</h1>
                    <p style={{ opacity: 0.9, marginBottom: '16px' }}>{details.nom_obligation || 'Obligation'}</p>
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Percent size={20} />
                            <span>Taux: {details.taux_interet?.toFixed(2) || 'N/A'}%</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Building2 size={20} />
                            <span>Émetteur: {details.emetteur || 'N/A'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={20} />
                            <span>Échéance: {details.date_echeance ? new Date(details.date_echeance).toLocaleDateString('fr-FR') : 'N/A'}</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', padding: '16px', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>Cours Clôture</div>
                            <div style={{ fontSize: '28px', fontWeight: 700 }}>{latestCours?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) || '-'}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>Volume (FCFA)</div>
                            <div style={{ fontSize: '28px', fontWeight: 700 }}>{latestVolume?.toLocaleString('fr-FR') || '-'}</div>
                        </div>
                    </div>
                </div>

                <div style={{ background: 'var(--bg-box)', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                    <h2 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>Historique des Cours (derniers 20)</h2>
                    {details.cotations.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                    <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                                    <th style={{ padding: '12px', textAlign: 'right' }}>Cours</th>
                                    <th style={{ padding: '12px', textAlign: 'right' }}>Volume</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.cotations.slice(0, 20).map((c, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '12px' }}>{new Date(c.date_cotation).toLocaleDateString('fr-FR')}</td>
                                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>{c.cours_cloture?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                                        <td style={{ padding: '12px', textAlign: 'right' }}>{c.volume_titres?.toLocaleString('fr-FR') || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Aucun historique disponible</div>
                    )}
                </div>
            </div>
        </SiteLayout>
    );
};

export default ObligationDetailsScreen;

